/**
 * Utility functions for checking if field inputs are complete
 * before triggering AI validation calls.
 */

export const isEmailComplete = (email: string): boolean => {
  if (!email) return false;
  // Basic check: must contain '@' and a domain extension
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isPhoneComplete = (phone: string): boolean => {
  if (!phone) return false;
  // Clean phone number (digits only) and check length
  const digits = phone.replace(/\D/g, '');
  return digits.length >= 7; // Minimal length for local/intl numbers
};

export const isGstComplete = (gst: string): boolean => {
  if (!gst) return false;
  // Indian GST is usually 15 characters
  const cleanGst = gst.trim().replace(/[\s-]/g, '');
  return cleanGst.length === 15;
};

export const isPanComplete = (pan: string): boolean => {
  if (!pan) return false;
  // Indian PAN is usually 10 characters
  const cleanPan = pan.trim().replace(/[\s-]/g, '');
  return cleanPan.length === 10;
};

export interface AddressValue {
  country?: string;
  pincode?: string;
  [key: string]: any;
}

export const isAddressComplete = (address: AddressValue | null | undefined): boolean => {
  if (!address) return false;
  const { country, pincode } = address;
  return !!(country && country.trim() && pincode && pincode.trim());
};

export interface DimensionValue {
  length?: string | number;
  width?: string | number;
  height?: string | number;
  weight?: string | number;
}

export const isDimensionComplete = (dimension: DimensionValue | null | undefined): boolean => {
  if (!dimension) return false;
  const { length, width, height, weight } = dimension;

  const parseVal = (v: any) => {
    if (v === undefined || v === null || v === '') return 0;
    const num = Number(v);
    return isNaN(num) ? 0 : num;
  };

  const l = parseVal(length);
  const w = parseVal(width);
  const ht = parseVal(height);
  const wt = parseVal(weight);

  return l > 0 && w > 0 && ht > 0 && wt > 0;
};

/**
 * Checks if a value is complete based on its validation type.
 */
export const isValueComplete = (type: string, value: any): boolean => {
  switch (type.toLowerCase()) {
    case 'email':
      return typeof value === 'string' && isEmailComplete(value);
    case 'phone':
      return typeof value === 'string' && isPhoneComplete(value);
    case 'gst':
      return typeof value === 'string' && isGstComplete(value);
    case 'pan':
      return typeof value === 'string' && isPanComplete(value);
    case 'address':
      return isAddressComplete(value);
    case 'dimension':
      return value !== undefined && value !== null && value !== '' && !isNaN(Number(value)) && Number(value) > 0;
    default:
      return false;
  }
};
